import { motion } from "framer-motion";
import { TrendingUp, Brain, LineChart, Zap, AlertTriangle, ArrowUpRight } from "lucide-react";
import { SparkLine } from "@/components/MiniChart";

const INTEL = [
  {
    tag: "FX · Prop",
    icon: TrendingUp,
    signal: "BULL",
    accent: "green",
    color: "#D9F99D",
    headline: "Retail FX is shedding its top revenue teams — poaching cycles now clock 30 days.",
    points: [
      "MT5 migrations + margin compression are forcing brokers to reshuffle dealing, sales & risk in Q1.",
      "Referral loops now yield <12% of senior hires; structured headhunting is the new moat.",
      "Compensation inflation for Heads of Sales up ~38% YoY across MENA & SEA.",
    ],
    meta: "Q1 2026 · High Conviction",
    chart: [18, 24, 22, 30, 28, 36, 32, 44, 40, 52, 48, 64, 70, 78],
  },
  {
    tag: "AI · Quant",
    icon: Brain,
    signal: "CRITICAL",
    accent: "purple",
    color: "#A78BFA",
    headline: "The AI talent shortage is now a hedge‑fund problem — quants are the new ML engineers.",
    points: [
      "5,000 global researchers are contested by both frontier labs and systematic desks.",
      "Agencies that source cross‑domain (quant ↔ AI) close 3× faster at 2× retention.",
      "Total comp for senior research leads now breaches $1.2M in NY / London / Zurich.",
    ],
    meta: "Feb 2026 · Tier‑1",
    chart: [12, 15, 14, 20, 18, 26, 22, 34, 30, 48, 42, 68, 74, 92],
  },
  {
    tag: "Prop Firms",
    icon: LineChart,
    signal: "WATCH",
    accent: "yellow",
    color: "#FDE68A",
    headline: "Prop firms scaling 4× — but the wrong operators are killing growth.",
    points: [
      "Post‑regulation, CCO / CTO / Head of Trading hires now drive 70% of enterprise value.",
      "Generic recruiters can’t map hybrid retail‑funnel + institutional‑risk profiles.",
      "Search firms with on‑desk operators are closing mandates in <28 days.",
    ],
    meta: "Mar 2026 · Rising",
    chart: [22, 24, 26, 25, 30, 28, 34, 32, 40, 38, 46, 44, 52, 58],
  },
];

const SIGNAL_BG = {
  BULL:    "bg-[#D9F99D]/10 border-[#D9F99D]/30 text-[#D9F99D]",
  WATCH:   "bg-[#FDE68A]/10 border-[#FDE68A]/30 text-[#FDE68A]",
  CRITICAL:"bg-[#A78BFA]/10 border-[#A78BFA]/30 text-[#A78BFA]",
};

export default function Insights() {
  return (
    <section
      id="insights"
      data-testid="insights-section"
      className="relative py-20 sm:py-24 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[#D9F99D]/90">
              <Zap size={14} />
              Market Intelligence · 2026
            </div>
            <h2
              data-testid="insights-heading"
              className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white"
            >
              Signals from the <span className="zx-hi">desk.</span>
            </h2>
          </div>
          <p className="max-w-md text-neutral-200 text-[15px] leading-relaxed">
            What we&apos;re seeing across FX, prop, and AI hiring right now — straight from the engagements we&apos;re running.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {INTEL.map((p, i) => (
            <motion.article
              key={p.headline}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.1 }}
              data-testid={`insight-card-${i}`}
              data-accent={p.accent}
              className="zx-card group"
            >
              {/* soft accent blob */}
              <div
                aria-hidden
                className="absolute -top-14 -right-10 w-56 h-56 rounded-full opacity-55"
                style={{ background: `radial-gradient(circle, ${p.color}24, transparent 70%)`, filter: "blur(26px)" }}
              />
              {/* terminal header */}
              <div className="flex items-center justify-between px-7 py-4 border-b border-white/5 bg-black/30 relative">
                <div
                  className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em]"
                  style={{ color: p.color }}
                >
                  <p.icon size={14} />
                  {p.tag}
                </div>
                <div className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] ${SIGNAL_BG[p.signal]}`}>
                  <AlertTriangle size={10} />
                  {p.signal}
                </div>
              </div>

              <div className="p-7 relative">
                {/* Mini chart */}
                <div className="relative rounded-xl border border-white/5 bg-black/20 overflow-hidden p-3">
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">
                    <span>Demand Index</span>
                    <span className="inline-flex items-center gap-1" style={{ color: p.color }}>
                      <ArrowUpRight size={11} /> +{(p.chart.at(-1) - p.chart[0]).toFixed(0)}
                    </span>
                  </div>
                  <SparkLine values={p.chart} color={p.color} width={280} height={72} />
                </div>

                <h3 className="font-display text-xl lg:text-[22px] text-white leading-snug mt-6">
                  {p.headline}
                </h3>
                <ul className="mt-5 space-y-3">
                  {p.points.map((pt, k) => (
                    <li key={k} className="flex gap-3 text-sm text-neutral-200 leading-relaxed">
                      <span
                        className="mt-2 h-1 w-3 rounded-sm shrink-0"
                        style={{ background: p.color, opacity: 0.7 }}
                      />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 pt-5 border-t border-white/5 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">
                  <span>{p.meta}</span>
                  <span className="inline-flex items-center gap-2" style={{ color: p.color }}>
                    <span className="h-1.5 w-1.5 rounded-full zx-pulse" style={{ background: p.color }} />
                    Live
                  </span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
